import { PROGRESS_TEXT_COLORS, getProgressTextColors } from "./textColors";
import React, { useEffect } from "react";
import { Footer } from "../../components/Footer";
import { ProgressBanner } from "./ProgressBanner";
import { ProgressTable } from "./ProgressTable";
import { ProgressRowItem } from "./ProgressRowItem";
import { ProgressRowItemDetail } from "./ProgressRowItemDetail";
import { ProgressCardItem } from "./ProgressCardItem";
import { ProgressCardItemDetail } from "./ProgressCardItemDetail";
import { KpiCard } from "./KpiCard";
import { DetailedStats } from "./DetailedStats";
import { GlobalRanking } from "./GlobalRanking";
import { useMoodle } from "../../contexts/MoodleContext";
import { useAuth } from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/config";
import { useTheme } from "../../contexts/ThemeContext";
import { mapWithConcurrency } from "../../utils/concurrency";
import { resolveCourseCertificateCmid } from "../../services/moodle/courseCertificates";
import { resolveCourseGradeAndProgress } from "../../services/moodle/courseFinalGrade";
import { getUserProgressSummary } from "../../services/moodle/courses";
import { Skeleton } from "@mui/material";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const CERT_CONCURRENCY = 6;

const Progress = () => {
  const [loadingGrades, setLoadingGrades] = React.useState(true);
  const [sortConfig, setSortConfig] = React.useState({ key: null, direction: "asc" });
  const [filterStatus, setFilterStatus] = React.useState("all"); // "all" | "completed" | "in_progress"
  const moodle = useMoodle();
  const theme = useTheme();
  const textColors = getProgressTextColors(theme.mode);
  const { lang, user } = useAuth();
  const { config } = useConfig();
  const [certificates, setCertificates] = React.useState({});
  const [finalGrades, setFinalGrades] = React.useState({});
  const [courseProgress, setCourseProgress] = React.useState({});
  const [summaryData, setSummaryData] = React.useState(null);

  const [selectedCourseId, setSelectedCourseId] = React.useState(null);
  const [selectedCourseName, setSelectedCourseName] = React.useState("");
  const [detailData, setDetailData] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailTitles, setDetailTitles] = React.useState(null);
  const [pluginState, setPluginState] = React.useState("loading"); // "loading" | "success" | "failed"

  const availableCourses = React.useMemo(
    () => moodle.courses ?? null,
    [moodle.courses]
  );

  const courseIdsKey = React.useMemo(
    () => (availableCourses ?? []).map((c) => c.id).join(","),
    [availableCourses]
  );

  // Fast path: Consolidated server-side summary from local_tipddy_performance plugin
  useEffect(() => {
    if (!user?.token) return;
    let cancelled = false;

    getUserProgressSummary({
      token: user.token,
      userid: user.id || 0,
      baseUrl: config.baseURL,
    })
      .then((data) => {
        if (!cancelled) {
          if (data && data.courses && Array.isArray(data.courses)) {
            setSummaryData(data);
            setPluginState("success");
            setLoadingGrades(false);
          } else {
            setPluginState("failed");
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn("Tipddy Performance plugin not active yet, using fallback resolution:", err.message);
          setPluginState("failed");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.token, user?.id, config.baseURL]);

  useEffect(() => {
    if (pluginState !== "failed") return;
    const courses = availableCourses;
    if (!courses || !courses.length || !user?.token) return;

    let cancelled = false;
    setCertificates({});

    mapWithConcurrency(
      courses,
      CERT_CONCURRENCY,
      (c) => resolveCourseCertificateCmid({ token: user.token, courseId: c.id }),
      (c, _i, cmid) => {
        if (!cancelled) {
          setCertificates((prev) => ({ ...prev, [c.id]: cmid }));
        }
      }
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pluginState, courseIdsKey, user?.token]);

  useEffect(() => {
    if (pluginState !== "failed") return;
    const courses = availableCourses ?? [];
    if (!courses.length || !user?.token) {
      if (moodle.courses) {
        setLoadingGrades(false);
      }
      return;
    }

    let cancelled = false;
    setLoadingGrades(true);
    setFinalGrades({});
    setCourseProgress({});

    mapWithConcurrency(
      courses,
      CERT_CONCURRENCY,
      (c) => resolveCourseGradeAndProgress({ token: user.token, id: user.id, courseId: c.id }),
      (c, _i, result) => {
        if (!cancelled) {
          setFinalGrades((prev) => ({ ...prev, [c.id]: result?.finalGrade ?? null }));
          setCourseProgress((prev) => ({ ...prev, [c.id]: result?.progress ?? null }));
        }
      }
    ).finally(() => {
      if (!cancelled) {
        setLoadingGrades(false);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pluginState, courseIdsKey, user?.token, user?.id]);

  const tableTitles = React.useMemo(() => {
    if (!lang || !lang["History"]) return null;
    return [
      {
        label: lang["History"].first_column,
        key: "name",
        sortable: true,
        icon: <AutoStoriesIcon sx={{ fontSize: 16 }} />,
      },
      { label: lang["History"].progress_column, key: "progress", sortable: true, width: "110px" },
      { label: lang["History"].second_column, key: "grade", sortable: true, width: "130px" },
      { label: lang["History"].third_column, key: "startdate", sortable: true, width: "120px" },
      { label: lang["History"].fourth_column, key: "enddate", sortable: true, width: "140px" },
      { label: lang["History"].fifth_column, key: "certificate", sortable: true, width: "140px" },
      ...(config?.opciones?.includes("encuesta") ? [{ label: lang["History"].sixth_column, key: "survey", sortable: false, width: "100px" }] : []),
    ];
  }, [lang, config]);

  const handleSort = React.useCallback((key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      const defaultDir = key === "name" ? "asc" : "desc";
      return { key, direction: defaultDir };
    });
  }, []);

  useEffect(() => {
    !moodle.courses && moodle.getCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleCourses = React.useMemo(
    () => summaryData?.courses ?? availableCourses ?? [],
    [summaryData, availableCourses]
  );

  const isCompletedCourse = React.useCallback(
    (c) => {
      if (c.is_completed !== undefined) {
        return Boolean(c.is_completed);
      }
      const certCmid =
        c.certificate_cmid && c.certificate_cmid > 0
          ? c.certificate_cmid
          : certificates[c.id];
      const prog = certCmid
        ? 100
        : (courseProgress[c.id] ??
          (c.progress !== null && c.progress !== undefined
            ? Number(c.progress)
            : null));
      return prog !== null && Number(prog) >= 100;
    },
    [certificates, courseProgress]
  );

  const isInProgressCourse = React.useCallback(
    (c) => {
      if (c.is_completed !== undefined) {
        const grade = c.final_grade ?? "-";
        return (
          !c.is_completed &&
          ((c.progress !== null && Number(c.progress) > 0) || grade !== "-")
        );
      }
      const certCmid =
        c.certificate_cmid && c.certificate_cmid > 0
          ? c.certificate_cmid
          : certificates[c.id];
      const prog = certCmid
        ? 100
        : (courseProgress[c.id] ??
          (c.progress !== null && c.progress !== undefined
            ? Number(c.progress)
            : null));
      return prog !== null && Number(prog) > 0 && Number(prog) < 100;
    },
    [certificates, courseProgress]
  );

  const kpis = React.useMemo(() => {
    const total = visibleCourses.length;
    const completed = visibleCourses.filter(isCompletedCourse).length;
    const inProgress = visibleCourses.filter(isInProgressCourse).length;
    return { total, completed, inProgress };
  }, [visibleCourses, isCompletedCourse, isInProgressCourse]);

  const filteredCourses = React.useMemo(() => {
    if (filterStatus === "completed") {
      return visibleCourses.filter(isCompletedCourse);
    }
    if (filterStatus === "in_progress") {
      return visibleCourses.filter(isInProgressCourse);
    }
    return visibleCourses;
  }, [visibleCourses, filterStatus, isCompletedCourse, isInProgressCourse]);

  const sortedCourses = React.useMemo(() => {
    if (!sortConfig.key) return filteredCourses;

    const { key, direction } = sortConfig;
    const modifier = direction === "asc" ? 1 : -1;

    return [...filteredCourses].sort((a, b) => {
      if (key === "name") {
        const nameA = a.displayname || a.fullname || "";
        const nameB = b.displayname || b.fullname || "";
        return nameA.localeCompare(nameB, "es", { sensitivity: "base", numeric: true }) * modifier;
      }

      if (key === "progress") {
        const getProg = (c) => {
          const certCmid = (c.certificate_cmid && c.certificate_cmid > 0) ? c.certificate_cmid : certificates[c.id];
          if (certCmid) return 100;
          if (typeof c.progress === "number") return c.progress;
          const p = courseProgress[c.id] ?? (c.progress !== null && c.progress !== undefined ? Number(c.progress) : null);
          return p !== null && !isNaN(p) ? Number(p) : -1;
        };
        const progA = getProg(a);
        const progB = getProg(b);
        if (progA !== progB) {
          return (progA - progB) * modifier;
        }
        return (a.displayname || "").localeCompare(b.displayname || "", "es");
      }

      if (key === "grade") {
        const getGrade = (c) => {
          const raw = c.final_grade ?? (finalGrades[c.id]?.formatted ?? "-");
          if (!raw || raw === "-") return null;
          const cleaned = String(raw).replace(",", ".").replace(/[^0-9.]/g, "");
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? null : parsed;
        };
        const gradeA = getGrade(a);
        const gradeB = getGrade(b);
        const hasGradeA = gradeA !== null;
        const hasGradeB = gradeB !== null;
        if (hasGradeA !== hasGradeB) {
          return hasGradeA ? -1 : 1;
        }
        if (!hasGradeA && !hasGradeB) {
          return (a.displayname || "").localeCompare(b.displayname || "", "es");
        }
        const diff = gradeA - gradeB;
        return diff !== 0 ? diff * modifier : (a.displayname || "").localeCompare(b.displayname || "", "es");
      }

      if (key === "startdate") {
        const dateA = a.startdate ? Number(a.startdate) : 0;
        const dateB = b.startdate ? Number(b.startdate) : 0;
        if (dateA !== dateB) {
          return (dateA - dateB) * modifier;
        }
        return (a.displayname || "").localeCompare(b.displayname || "", "es");
      }

      if (key === "enddate") {
        const hasEndA = Boolean(a.enddate && Number(a.enddate) !== 0);
        const hasEndB = Boolean(b.enddate && Number(b.enddate) !== 0);
        if (hasEndA !== hasEndB) {
          return hasEndA ? -1 : 1;
        }
        if (!hasEndA && !hasEndB) {
          return (a.displayname || "").localeCompare(b.displayname || "", "es");
        }
        const diff = Number(a.enddate) - Number(b.enddate);
        return diff !== 0 ? diff * modifier : (a.displayname || "").localeCompare(b.displayname || "", "es");
      }

      if (key === "certificate") {
        const hasCert = (c) => {
          const certCmid = (c.certificate_cmid && c.certificate_cmid > 0) ? c.certificate_cmid : certificates[c.id];
          return certCmid ? 1 : 0;
        };
        const certA = hasCert(a);
        const certB = hasCert(b);
        if (certA !== certB) {
          return (certA - certB) * modifier;
        }
        return (a.displayname || "").localeCompare(b.displayname || "", "es");
      }

    });
  }, [filteredCourses, sortConfig, certificates, finalGrades, courseProgress]);

  React.useEffect(() => {
    if (lang && !detailTitles) {
      setDetailTitles([
        {
          label: lang["History"].inner_first_column,
          icon: <AutoStoriesIcon sx={{ fontSize: 16 }} />,
        },
        { label: lang["History"].inner_second_column, width: "120px" },
        ...(config?.opciones?.includes("feedback") ? [
          { label: lang["History"].inner_fourth_column, width: "180px" },
          { label: lang["History"].inner_fifth_column, width: "180px" },
        ] : []),
      ]);
    }
  }, [lang, config, detailTitles]);

  async function fetchDetail(courseId) {
    setDetailLoading(true);
    setDetailData(null);
    try {
      const [gradesData] = await moodle.getProgressDetail(courseId);
      const rawItems = gradesData?.usergrades?.[0]?.gradeitems ?? [];
      const filterdata = rawItems
        .filter((d) => d.itemtype !== "course" && d.itemtype !== "category")
        .filter((d) => d.itemmodule !== "folder")
        .filter((d) => d.itemname !== null && d.itemname !== "");
      setDetailData(filterdata);
    } catch (e) {
      console.log(e.message);
      setDetailData([]);
    } finally {
      setDetailLoading(false);
    }
  }

  const handleRowClick = (course) => {
    setSelectedCourseId(course.id);
    setSelectedCourseName(course.displayname || course.fullname);
    fetchDetail(course.id);
  };

  const handleBack = () => {
    setSelectedCourseId(null);
    setSelectedCourseName("");
    setDetailData(null);
  };

  return (
    <>
      {lang && (
        <>
          <ProgressBanner />

          <div
            className="progress-page flex flex-col items-center w-full pt-8 pb-16 min-h-dvh bg-md-surface text-md-on-surface font-montserrat"
            style={{
              gap: "36px",
              "--progress-text-primary": PROGRESS_TEXT_COLORS.dark.primary,
              "--progress-text-secondary": PROGRESS_TEXT_COLORS.dark.secondary,
            }}
          >
            <div className="page-content-shell space-y-3">
              <style>{`
                .progress-panels-wrapper {
                  position: relative;
                  overflow: hidden;
                  padding: 2px 2px 6px 2px;
                  margin: -2px -2px -6px -2px;
                }
                .progress-panel {
                  width: 100%;
                  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
                  will-change: transform;
                }
                .progress-panel-list {
                  transform: translateX(0);
                  opacity: 1;
                }
                .progress-panel-list.slide-out {
                  transform: translateX(-100%);
                  opacity: 0;
                  position: absolute;
                  top: 0; left: 0; right: 0;
                  pointer-events: none;
                }
                .progress-panel-detail {
                  transform: translateX(100%);
                  opacity: 0;
                  position: absolute;
                  top: 0; left: 0; right: 0;
                  pointer-events: none;
                }
                .progress-panel-detail.slide-in {
                  transform: translateX(0);
                  opacity: 1;
                  position: relative;
                  pointer-events: auto;
                }
                .progress-desktop-table {
                  display: none;
                }
                .progress-mobile-cards {
                  display: flex;
                  flex-direction: column;
                  gap: 12px;
                }
                @media (min-width: 768px) {
                  .progress-desktop-table {
                    display: block !important;
                  }
                  .progress-mobile-cards {
                    display: none !important;
                  }
                }
                .progress-row-hover:hover {
                  background-color: ${theme.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"};
                }
                .gr-header {
                  display: flex;
                  align-items: center;
                  gap: 12px;
                  margin-bottom: 28px;
                  border-bottom: 1px solid ${theme.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"};
                  padding-bottom: 12px;
                }
                .gr-header-icon {
                  font-size: 26px;
                  line-height: 1;
                }
                .gr-header h2 {
                  margin: 0;
                  font-size: clamp(20px, 3vw, 30px);
                  font-weight: 400;
                  line-height: 1.2;
                  color: var(--progress-text-primary);
                }
                .gr-header h2 strong {
                  font-weight: 800;
                  border-bottom: 3px solid #ecb236;
                  padding-bottom: 11px;
                  display: inline-block;
                }
                .gr-sep {
                  color: var(--progress-text-secondary);
                  font-weight: 300;
                  margin: 0 4px;
                }
                .gr-header-subtitle {
                  font-weight: 400;
                }
                @media (max-width: 767px) {
                  .gr-header h2 {
                    font-size: 19px !important;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 2px;
                    line-height: 1.25;
                  }
                  .gr-header h2 strong {
                    border-bottom: 2px solid #ecb236;
                    padding-bottom: 3px;
                    margin-bottom: 2px;
                  }
                  .gr-sep {
                    display: none !important;
                  }
                  .gr-header-subtitle {
                    font-size: 13px !important;
                    color: var(--progress-text-secondary) !important;
                    font-weight: 500 !important;
                  }
                }
              `}</style>

              <div className="gr-header">
                {selectedCourseId ? (
                  <>
                    <button
                      onClick={handleBack}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors duration-150 mr-2 shrink-0"
                      style={{
                        backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                        color: textColors.primary,
                      }}
                    >
                      <ArrowBackIcon sx={{ fontSize: 18 }} />
                      Volver
                    </button>
                    <h2>
                      <strong>{lang["History"]?.inner_title || "Detalle"}</strong>{" "}
                      <span className="gr-sep">|</span>{" "}
                      <span className="gr-header-subtitle" style={{ color: "#ecb236" }}>{selectedCourseName}</span>
                    </h2>
                  </>
                ) : (
                  <>
                    <span className="gr-header-icon text-md-primary-container flex items-center justify-center shrink-0" role="img" aria-label="progreso">
                      <TrendingUpIcon sx={{ fontSize: 32 }} />
                    </span>
                    <h2>
                      <strong>{lang["History"].first_title}</strong>{" "}
                      <span className="gr-sep">|</span>{" "}
                      <span className="gr-header-subtitle">{lang["History"].first_column}</span>
                    </h2>
                  </>
                )}
              </div>

              {!selectedCourseId && (
                <div className="grid grid-cols-3 tablet:flex tablet:flex-wrap gap-2 tablet:gap-4 w-full pb-2">
                  <KpiCard
                    label={lang["History"].kpi_total_courses}
                    value={
                      loadingGrades ? (
                        <Skeleton
                          variant="text"
                          width={48}
                          sx={{ bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)" }}
                        />
                      ) : (
                        kpis.total
                      )
                    }
                    icon={<AutoStoriesIcon />}
                    active={filterStatus === "all"}
                    onClick={() => setFilterStatus("all")}
                  />
                  <KpiCard
                    label={lang["History"].kpi_completed_courses}
                    value={
                      loadingGrades ? (
                        <Skeleton
                          variant="text"
                          width={48}
                          sx={{ bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)" }}
                        />
                      ) : (
                        kpis.completed
                      )
                    }
                    icon={<CheckCircleIcon />}
                    active={filterStatus === "completed"}
                    onClick={() => setFilterStatus("completed")}
                  />
                  <KpiCard
                    label={lang["History"].kpi_courses_in_progress}
                    value={
                      loadingGrades ? (
                        <Skeleton
                          variant="text"
                          width={48}
                          sx={{ bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)" }}
                        />
                      ) : (
                        kpis.inProgress
                      )
                    }
                    icon={<SignalCellularAltIcon />}
                    active={filterStatus === "in_progress"}
                    onClick={() => setFilterStatus("in_progress")}
                  />
                </div>
              )}

              <div className="progress-panels-wrapper">
                {/* Panel: lista de cursos */}
                <div className={`progress-panel progress-panel-list${selectedCourseId ? " slide-out" : ""}`}>
                  {/* Vista Desktop: Tabla completa */}
                  <div className="progress-desktop-table">
                    <ProgressTable
                      headers={tableTitles}
                      sortConfig={sortConfig}
                      onSort={handleSort}
                    >
                      {(!summaryData && !moodle.courses) || loadingGrades ? (
                        [...Array(6)].map((_, i) => (
                          <tr key={`skel-${i}`}>
                            {[...Array(tableTitles?.length || 6)].map((_, j) => (
                              <td
                                key={j}
                                className={`p-4 ${j === 0 ? "rounded-l-md" : j === (tableTitles?.length || 6) - 1 ? "rounded-r-md" : ""}`}
                                style={{
                                  backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.02)" : "#FFFFFF",
                                  borderTop: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid #E2E8F0",
                                  borderBottom: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid #E2E8F0",
                                  borderLeft: j === 0 ? (theme.mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid #E2E8F0") : undefined,
                                  borderRight: j === (tableTitles?.length || 6) - 1 ? (theme.mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid #E2E8F0") : undefined,
                                }}
                              >
                                <Skeleton
                                  variant="rectangular"
                                  height={20}
                                  sx={{
                                    borderRadius: "4px",
                                    bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                                    width: j === 0 ? "80%" : j === 1 ? "80px" : "60%",
                                  }}
                                />
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : sortedCourses.length === 0 ? (
                        <tr>
                          <td
                            colSpan={tableTitles?.length || 6}
                            className="p-12 text-center text-[max(1rem,16px)] font-medium font-montserrat rounded-md"
                            style={{
                              color: textColors.primary,
                              border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid #E2E8F0",
                              backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.02)" : "#FFFFFF",
                            }}
                          >
                            {filterStatus === "completed"
                              ? "No tienes cursos completados todavía."
                              : filterStatus === "in_progress"
                              ? "No tienes cursos en progreso actualmente."
                              : "No se encontraron cursos."}
                          </td>
                        </tr>
                      ) : (
                        sortedCourses.map((c, idx) => {
                          const grade = c.final_grade ?? (finalGrades[c.id]?.formatted ?? "-");
                          const certCmid = (c.certificate_cmid && c.certificate_cmid > 0)
                            ? c.certificate_cmid
                            : certificates[c.id];
                          const prog = certCmid
                            ? 100
                            : (typeof c.progress === "number"
                                ? c.progress
                                : (courseProgress[c.id] ?? (c.progress !== null && c.progress !== undefined ? Number(c.progress) : null)));
                          return (
                            <ProgressRowItem
                              key={c.id ?? idx}
                              course={c}
                              grade={grade}
                              progress={prog}
                              lang={lang}
                              certificateCmid={certCmid}
                              includeEncuesta={config.opciones.includes("encuesta")}
                              rowIndex={idx}
                              onRowClick={handleRowClick}
                            />
                          );
                        })
                      )}
                    </ProgressTable>
                  </div>

                  {/* Vista Mobile: Tarjetas nativas sin desborde */}
                  <div className="progress-mobile-cards">
                    {(!summaryData && !moodle.courses) || loadingGrades ? (
                      [...Array(3)].map((_, i) => (
                        <Skeleton
                          key={`mob-skel-${i}`}
                          variant="rectangular"
                          height={120}
                          sx={{
                            borderRadius: "12px",
                            bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                          }}
                        />
                      ))
                    ) : sortedCourses.length === 0 ? (
                      <div
                        className="p-8 text-center text-sm font-medium font-montserrat rounded-xl"
                        style={{
                          color: textColors.primary,
                          border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid #E2E8F0",
                          backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.02)" : "#FFFFFF",
                        }}
                      >
                        {filterStatus === "completed"
                          ? "No tienes cursos completados todavía."
                          : filterStatus === "in_progress"
                          ? "No tienes cursos en progreso actualmente."
                          : "No se encontraron cursos."}
                      </div>
                    ) : (
                      sortedCourses.map((c, idx) => {
                        const grade = c.final_grade ?? (finalGrades[c.id]?.formatted ?? "-");
                        const certCmid = (c.certificate_cmid && c.certificate_cmid > 0)
                          ? c.certificate_cmid
                          : certificates[c.id];
                        const prog = certCmid
                          ? 100
                          : (typeof c.progress === "number"
                              ? c.progress
                              : (courseProgress[c.id] ?? (c.progress !== null && c.progress !== undefined ? Number(c.progress) : null)));
                        return (
                          <ProgressCardItem
                            key={`mob-${c.id ?? idx}`}
                            course={c}
                            grade={grade}
                            progress={prog}
                            lang={lang}
                            certificateCmid={certCmid}
                            rowIndex={idx}
                            onRowClick={handleRowClick}
                          />
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Panel: detalle del curso */}
                <div className={`progress-panel progress-panel-detail${selectedCourseId ? " slide-in" : ""}`}>
                  {detailLoading ? (
                    <div className="flex flex-col gap-2 p-4">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton
                          key={i}
                          variant="rectangular"
                          height={48}
                          sx={{
                            borderRadius: "6px",
                            bgcolor: theme.mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)",
                          }}
                        />
                      ))}
                    </div>
                  ) : detailData && detailTitles ? (
                    detailData.length > 0 ? (
                      <>
                        <div className="progress-desktop-table">
                          <ProgressTable headers={detailTitles}>
                            {detailData.map((d, idx) => (
                              <ProgressRowItemDetail
                                key={idx}
                                detail={d}
                                index={idx}
                                includeFeedback={config.opciones.includes("feedback")}
                              />
                            ))}
                          </ProgressTable>
                        </div>
                        <div className="progress-mobile-cards">
                          {detailData.map((d, idx) => (
                            <ProgressCardItemDetail
                              key={`mob-detail-${idx}`}
                              detail={d}
                              index={idx}
                              includeFeedback={config.opciones.includes("feedback")}
                            />
                          ))}
                        </div>
                      </>
                    ) : (
                      <div
                        className="flex flex-col items-center justify-center p-8 sm:p-12 rounded-xl text-center shadow-sm"
                        style={{
                          backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                          border: `1px solid ${theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                        }}
                      >
                        <p
                          className="font-lato font-medium text-[max(1rem,16px)]"
                          style={{ color: textColors.primary }}
                        >
                          {lang["History"]?.no_evaluations || "Este curso se aprueba por visualización de contenidos y no registra calificaciones numéricas."}
                        </p>
                      </div>
                    )
                  ) : null}
                </div>
              </div>
            </div>

            <div className="page-content-shell">
              <GlobalRanking />
            </div>

            <div className="page-content-shell">
              <DetailedStats />
            </div>
          </div>
          <Footer />
        </>
      )}
    </>
  );
};

export default Progress;
